/**
 * How long a scale run's lines are and how they stand in the play area. Pure: the scene hands in
 * its measurements (OSMD px for widths, device px for heights) and draws with the answers.
 */

/**
 * The longest a line of a run is drawn, in line gaps: the proportion of the engraved reference
 * (a system there is 106 line gaps from its left edge to its right).
 */
export const RUN_LINE_LENGTH_GAPS = 106;
/** OSMD keeps a page margin of one line gap on each side of a line. */
const OSMD_PAGE_MARGINS_GAPS = 2;

/**
 * The width (OSMD px) a line is laid out in: the reference's line length, never wider than a
 * lesson phrase is wide at the run's size, and never narrower than the line's natural width (it
 * would break onto a second line).
 */
export const runLineLayoutWidth = (line: {
  /** One line gap, in OSMD px. */
  lineGapPx: number;
  /** The widest line of the run at its natural spacing, in OSMD px. */
  naturalWidthPx: number;
  /** A lesson phrase's width at the run's size, in OSMD px. */
  lessonWidthPx: number;
}): number => {
  const { lineGapPx, naturalWidthPx, lessonWidthPx } = line;
  if (![lineGapPx, naturalWidthPx, lessonWidthPx].every((value) => value > 0)) {
    throw new Error('runLineLayoutWidth: every width must be positive');
  }
  const referenceWidthPx = (RUN_LINE_LENGTH_GAPS + OSMD_PAGE_MARGINS_GAPS) * lineGapPx;
  return Math.round(Math.max(naturalWidthPx, Math.min(lessonWidthPx, referenceWidthPx)));
};

/**
 * A line shorter than this share of the run's longest line (at natural spacing) is not stretched
 * to the full length: it keeps the longer lines' spacing and ends early, as an engraver leaves a
 * short last system. Longer lines are justified to the full length.
 */
export const RUN_LINE_JUSTIFY_SHARE = 2 / 3;

/**
 * Each line's layout width (OSMD px): the full length (see runLineLayoutWidth) for a line at least
 * two thirds as long as the longest; a shorter line stretched only as much as the longest is, so
 * every note of the run has the same room.
 */
export const runLineLayoutWidths = (lines: {
  lineGapPx: number;
  naturalWidthsPx: number[];
  lessonWidthPx: number;
}): number[] => {
  const { lineGapPx, naturalWidthsPx, lessonWidthPx } = lines;
  if (naturalWidthsPx.length === 0) return [];
  const longest = Math.max(...naturalWidthsPx);
  const full = runLineLayoutWidth({ lineGapPx, naturalWidthPx: longest, lessonWidthPx });
  return naturalWidthsPx.map((width) =>
    width >= RUN_LINE_JUSTIFY_SHARE * longest ? full : Math.round((width * full) / longest),
  );
};

/** How a run's lines are spaced down the play area (device px). */
export interface RunVerticalSpacing {
  /** Above the first line: the tempo marking's room, a share of the free space, a label row. */
  topPaddingPx: number;
  /** Between two lines: the least gap, a share of the free space, two label rows. */
  lineGapPx: number;
}

/**
 * The lines stand in the play area with a row of finger numbers above and below each, the tempo
 * marking's room on top and a margin below; the space they leave is shared out evenly above,
 * between and below them, so the run fills the area.
 */
export const runVerticalSpacing = (area: {
  heightPx: number;
  lineHeightsPx: number[];
  labelRowPx: number;
  topRoomPx: number;
  bottomMarginPx: number;
  /** The least gap between two lines, as a fraction of a line's height (with its labels). */
  minGapFraction: number;
}): RunVerticalSpacing => {
  const { heightPx, lineHeightsPx, labelRowPx, topRoomPx, bottomMarginPx, minGapFraction } = area;
  if (lineHeightsPx.length === 0) {
    return { topPaddingPx: topRoomPx + labelRowPx, lineGapPx: 2 * labelRowPx };
  }
  const withLabels = lineHeightsPx.map((height) => height + 2 * labelRowPx);
  const total = withLabels.reduce((sum, height) => sum + height, 0);
  const minGapPx = minGapFraction * (total / withLabels.length);
  const used = topRoomPx + bottomMarginPx + total + (withLabels.length - 1) * minGapPx;
  const share = Math.max(0, heightPx - used) / (withLabels.length + 1);
  return {
    topPaddingPx: topRoomPx + share + labelRowPx,
    lineGapPx: minGapPx + share + 2 * labelRowPx,
  };
};
